// First interactive feature - sits at the top of the page so personal stakes
// land before the user reads anything.
//
// New schema (per Checkpoint A): we don't return a flattering "new title".
// We return department + three pointed observations: what disappears, what
// they can't currently do, and who in their organisation has authority to
// make this transition. The last field is the lit fuse - for most senior
// leaders the honest answer is "no one with the explicit mandate".
//
// onResult fires with the latest fit so the parent can include it in the
// lead-notification email when the user later unlocks the diagnosis.

import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Link, Loader2, Search, AlertCircle, ArrowRight } from 'lucide-react';
import { postFit, type FitResult } from '../lib/api';
import { track } from '../lib/mixpanel';
import { intelligenceTier } from '../data/intelligenceTier';

const toSlug = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Normalise API-returned dept name against canonical list (API may append badge text)
function canonicalSlug(apiName: string): string {
  const { departments } = intelligenceTier;
  const match = departments.find(
    (d) => d.name.toLowerCase() === apiName.toLowerCase()
  ) ?? departments.find(
    (d) => apiName.toLowerCase().startsWith(d.name.toLowerCase())
  );
  return toSlug(match?.name ?? apiName);
}

export interface FitState {
  jobTitle: string;
  result: FitResult;
}

export default function WhereDoIFit({
  onResult,
}: {
  onResult?: (state: FitState) => void;
}) {
  const [jobTitle, setJobTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // clipboard not available
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jobTitle.trim() || loading) return;
    setLoading(true);
    setError(null);
    const trimmedTitle = jobTitle.trim();
    track('fit_submitted', { job_title: trimmedTitle }); // event 2
    try {
      const response = await postFit(trimmedTitle);
      setResult(response.result);
      onResult?.({ jobTitle: response.jobTitle, result: response.result });
      const dept = intelligenceTier.departments.find(d => d.name === response.result.department);
      track('fit_received', { // event 3
        job_title: response.jobTitle,
        department: response.result.department,
        tier: dept?.badgeType ?? 'unknown',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const submittedTitle = result ? jobTitle : '';

  return (
    <section id="where-do-i-fit" className="bg-paper-light border-b border-border py-16 scroll-mt-20">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="text-[12px] font-semibold tracking-mega uppercase text-green-700">
          Place yourself
        </div>
        <h2 className="font-display text-[28px] font-semibold tracking-[-0.015em] text-ink mt-3 mb-2">
          Where do you fit?
        </h2>
        <p className="text-[14px] text-ink-mute mb-6 max-w-[640px]">
          Type a job title. We'll map it onto the model, surface what disappears, what needs replacing, and where the authority gap sits.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-8 max-w-[640px]">
          <input
            type="text"
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder="e.g. Chief Marketing Officer"
            maxLength={200}
            required
            disabled={loading}
            className="flex-1 bg-white border border-border-strong rounded-md px-4 py-3 text-base sm:text-[14px] text-ink placeholder:text-stone-400 focus:outline-none focus:border-green-700 focus:ring-2 focus:ring-[rgba(139,184,216,0.45)] transition-colors disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={loading || jobTitle.trim().length === 0}
            className="bg-green-700 text-paper px-6 py-3 rounded-md text-[14px] font-medium flex items-center justify-center gap-2 hover:bg-green-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
            {loading ? 'Reading…' : 'Place me'}
          </button>
        </form>

        {error && (
          <div className="flex items-start gap-2 text-[13px] text-clay mb-4">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <AnimatePresence>
          {result && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="bg-white border border-border border-l-[3px] border-l-sky-deep rounded-lg p-6 max-w-[760px] shadow-elev-1"
            >
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-divider">
                <span className="text-[11px] font-semibold tracking-mega uppercase text-sky-deep">
                  Result for {submittedTitle || 'your role'}
                </span>
                <span className="ml-auto flex items-center gap-1.5 text-[11px] text-ink-faint tabular-nums">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  Generated just now
                </span>
              </div>

              <Field label="New department">
                <a
                  href={`#dept-${canonicalSlug(result.department)}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const slug = canonicalSlug(result.department);
                    window.dispatchEvent(new CustomEvent('expand-dept', { detail: { slug } }));
                    setTimeout(() => {
                      document.getElementById(`dept-${slug}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 50);
                  }}
                  className="inline-flex items-center gap-1.5 font-display text-[22px] font-semibold tracking-[-0.01em] text-green-700 border-b-2 border-green-200 hover:border-green-700 transition-colors"
                  style={{ textDecoration: 'none' }}
                >
                  {result.department}
                  <ArrowRight size={18} className="shrink-0 mt-0.5" />
                </a>
              </Field>

              <Field label="What disappears from your role">
                {result.whatDisappears}
              </Field>

              <Field label="What you'd need to be able to do - and currently can't">
                {result.whatYouCantDo}
              </Field>

              <Field label="Who has authority to make this happen" last>
                {result.whoHasAuthority}
              </Field>

              <div className="flex flex-col gap-3 pt-4 mt-2 border-t border-divider">
                <button
                  onClick={() => {
                    track('diagnosis_cta_clicked', { source: 'wdif_result' });
                    document.getElementById('email-gate')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="w-full bg-green-700 text-paper px-6 py-3.5 rounded-md text-[15px] font-medium flex items-center justify-center gap-2 hover:bg-green-800 active:bg-green-800 transition-colors"
                >
                  Get the full diagnosis
                  <ArrowRight size={16} className="shrink-0" />
                </button>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <span className="text-[12px] text-stone-500">Not in a position to act on this?</span>
                  <button
                    onClick={handleCopyLink}
                    className="inline-flex items-center gap-2 text-[12px] border border-border-strong rounded-md px-3 py-1.5 text-ink-mute bg-transparent hover:bg-paper-deep hover:text-ink transition-colors"
                  >
                    <Link size={12} />
                    {copied ? 'Link copied' : 'Forward to someone who is'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </section>
  );
}

function Field({
  label,
  children,
  last = false,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-2 md:gap-6 items-start md:items-center py-4 ${last ? 'pb-0' : 'border-b border-divider'
        }`}
    >
      <div className="text-[11px] font-semibold tracking-mega uppercase text-stone-500 leading-[1.5]">
        {label}
      </div>
      <div className="text-[14px] text-ink-soft leading-[1.7]">{children}</div>
    </div>
  );
}
